import React, { useState } from 'react';
import {
  Search,
  Filter,
  Plus,
  Edit,
  Eye,
  DollarSign,
  Package,
  TrendingUp,
  TrendingDown,
  BarChart3,
  Download,
  Settings,
  AlertTriangle,
  CheckCircle,
  Clock,
  Target,
  Activity,
  Zap,
  Shield,
  Building
} from 'lucide-react';
import { comprehensiveMedicationDatabase, ComprehensiveMedication } from '../../data/comprehensiveMedicationDatabase';
import { useLocation } from 'react-router-dom';

const Catalog: React.FC = () => {
  const location = useLocation();
  const [medications] = useState<ComprehensiveMedication[]>(comprehensiveMedicationDatabase);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSchedule, setSelectedSchedule] = useState<string>('all');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('profitabilityRank');
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('table');
  const [showPricing, setShowPricing] = useState(true);

  // Determine current view based on route
  const getCurrentView = () => {
    if (location.pathname === '/catalog/interactions') return 'interactions';
    if (location.pathname === '/catalog/pbs') return 'pbs';
    return 'catalog';
  };

  const currentView = getCurrentView();

  const filteredMedications = medications
    .filter(med => {
      const matchesSearch = med.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           med.genericName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           med.brandName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           med.activeIngredient.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesSchedule = selectedSchedule === 'all' || med.schedule === selectedSchedule;
      const matchesCategory = selectedCategory === 'all' || med.category === selectedCategory;
      
      return matchesSearch && matchesSchedule && matchesCategory;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'profitabilityRank':
          return a.profitabilityRank - b.profitabilityRank;
        case 'grossMargin':
          return b.grossMargin - a.grossMargin;
        case 'monthlyVolume':
          return b.monthlyVolume - a.monthlyVolume;
        case 'name':
          return a.name.localeCompare(b.name);
        case 'retailPrice':
          return b.retailPrice - a.retailPrice;
        default:
          return 0;
      }
    });

  const categories = medications.reduce((acc: string[], med) => {
    if (!acc.includes(med.category)) {
      acc.push(med.category);
    }
    return acc;
  }, []);
  const schedules = ['S2', 'S3', 'S4', 'S8', 'unscheduled'];

  const getScheduleBadgeClass = (schedule: string) => {
    switch (schedule) {
      case 'S8': return 'bg-red-100 text-red-800 border-red-200';
      case 'S4': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'S3': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'S2': return 'bg-blue-100 text-blue-800 border-blue-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getCategoryColor = (category: string) => {
    const colors: { [key: string]: string } = {
      'Cardiovascular': 'bg-red-100 text-red-800',
      'Endocrine': 'bg-blue-100 text-blue-800',
      'Respiratory': 'bg-green-100 text-green-800',
      'Gastrointestinal': 'bg-yellow-100 text-yellow-800',
      'Analgesic': 'bg-purple-100 text-purple-800',
      'Antibiotic': 'bg-pink-100 text-pink-800',
      'Mental Health': 'bg-indigo-100 text-indigo-800',
      'Immunosuppressant': 'bg-orange-100 text-orange-800',
      'Corticosteroid': 'bg-teal-100 text-teal-800',
      'Musculoskeletal': 'bg-cyan-100 text-cyan-800'
    };
    return colors[category] || 'bg-gray-100 text-gray-800';
  };

  const getProfitabilityColor = (rank: number) => {
    if (rank <= 10) return 'text-green-600';
    if (rank <= 25) return 'text-yellow-600';
    return 'text-red-600';
  };

  // Calculate summary statistics
  const totalMedications = medications.length;
  const averageMargin = medications.reduce((sum, med) => sum + med.grossMargin, 0) / totalMedications;
  const totalMonthlyVolume = medications.reduce((sum, med) => sum + med.monthlyVolume, 0);
  const highMarginMeds = medications.filter(med => med.grossMargin > 40).length;

  // Mock drug interactions data
  const drugInteractions = [
    {
      medication1: 'Warfarin',
      medication2: 'Aspirin',
      severity: 'Major',
      description: 'Increased risk of bleeding',
      management: 'Monitor INR closely, consider alternative analgesic'
    },
    {
      medication1: 'Digoxin',
      medication2: 'Furosemide',
      severity: 'Moderate',
      description: 'Increased digoxin toxicity risk',
      management: 'Monitor potassium levels and digoxin concentration'
    },
    {
      medication1: 'Metformin',
      medication2: 'Lisinopril',
      severity: 'Minor',
      description: 'Potential for enhanced hypoglycemic effect',
      management: 'Monitor blood glucose levels'
    },
    {
      medication1: 'Atorvastatin',
      medication2: 'Clarithromycin',
      severity: 'Major',
      description: 'Increased risk of myopathy and rhabdomyolysis',
      management: 'Avoid combination or reduce statin dose'
    },
    {
      medication1: 'Phenytoin',
      medication2: 'Carbamazepine',
      severity: 'Moderate',
      description: 'Altered anticonvulsant levels',
      management: 'Monitor drug levels and adjust doses accordingly'
    }
  ];

  // Mock PBS data
  const pbsMedications = medications.filter(med => med.pbsListed).map(med => ({
    ...med,
    pbsCode: `${Math.floor(Math.random() * 9000) + 1000}`,
    subsidyAmount: med.retailPrice * 0.7,
    patientCopayment: Math.min(med.retailPrice * 0.3, 42.50),
    safetyNetThreshold: 316.80
  }));

  if (currentView === 'interactions') {
    return (
      <div className="space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
              Drug Interactions
            </h1>
            <p className="text-gray-600 mt-2">Check for potential medication interactions and contraindications</p>
          </div>
          <div className="flex space-x-3">
            <button className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 flex items-center space-x-2">
              <Download className="h-4 w-4" />
              <span>Export</span>
            </button>
            <button className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 flex items-center space-x-2">
              <Zap className="h-4 w-4" />
              <span>Check Interaction</span>
            </button>
          </div>
        </div>

        {/* Interaction Checker */}
        <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4">Interaction Checker</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <select className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent">
              <option value="">Select first medication...</option>
              {medications.slice(0, 20).map(med => (
                <option key={med.id} value={med.id}>{med.name}</option>
              ))}
            </select>
            <select className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent">
              <option value="">Select second medication...</option>
              {medications.slice(0, 20).map(med => (
                <option key={med.id} value={med.id}>{med.name}</option>
              ))}
            </select>
          </div>
          <button className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700">
            Check for Interactions
          </button>
        </div>

        {/* Known Interactions */}
        <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-6">Known Drug Interactions</h3>
          <div className="space-y-4">
            {drugInteractions.map((interaction, index) => (
              <div key={index} className={`p-4 rounded-xl border ${
                interaction.severity === 'Major' ? 'bg-red-50 border-red-200' :
                interaction.severity === 'Moderate' ? 'bg-yellow-50 border-yellow-200' :
                'bg-blue-50 border-blue-200'
              }`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <h4 className="font-semibold text-gray-900">
                      {interaction.medication1} + {interaction.medication2}
                    </h4>
                    <span className={`px-3 py-1 text-sm font-medium rounded-full ${
                      interaction.severity === 'Major' ? 'bg-red-100 text-red-800' :
                      interaction.severity === 'Moderate' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-blue-100 text-blue-800'
                    }`}>
                      {interaction.severity}
                    </span>
                  </div>
                  <AlertTriangle className={`h-5 w-5 ${
                    interaction.severity === 'Major' ? 'text-red-600' :
                    interaction.severity === 'Moderate' ? 'text-yellow-600' :
                    'text-blue-600'
                  }`} />
                </div>
                <p className="text-gray-700 mb-2">{interaction.description}</p>
                <p className="text-sm text-gray-600"><strong>Management:</strong> {interaction.management}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (currentView === 'pbs') {
    return (
      <div className="space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
              PBS Lookup
            </h1>
            <p className="text-gray-600 mt-2">Pharmaceutical Benefits Scheme pricing and subsidies</p>
          </div>
          <div className="flex space-x-3">
            <button className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 flex items-center space-x-2">
              <Download className="h-4 w-4" />
              <span>Export</span>
            </button>
          </div>
        </div>

        {/* PBS Search */}
        <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Search PBS medications..."
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            <select className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent">
              <option value="all">All Categories</option>
              {categories.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
            <button className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700">
              Search PBS
            </button>
          </div>
        </div>

        {/* PBS Listed Medications */}
        <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-xl font-bold text-gray-900">PBS Listed Medications ({pbsMedications.length})</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50/50">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">Medication</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">PBS Code</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">Retail Price</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">Subsidy</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">Patient Copay</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">Category</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {pbsMedications.slice(0, 20).map((medication) => (
                  <tr key={medication.id} className="hover:bg-gray-50/50">
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-medium text-gray-900">{medication.name}</p>
                        <p className="text-sm text-gray-600">{medication.genericName}</p>
                        <p className="text-sm text-gray-500">{medication.brandName}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4 font-medium text-gray-900">{medication.pbsCode}</td>
                    <td className="px-6 py-4 font-medium text-gray-900">${medication.retailPrice.toFixed(2)}</td>
                    <td className="px-6 py-4 font-medium text-green-600">${medication.subsidyAmount.toFixed(2)}</td>
                    <td className="px-6 py-4 font-medium text-gray-900">${medication.patientCopayment.toFixed(2)}</td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 text-sm font-medium rounded-full ${getCategoryColor(medication.category)}`}>
                        {medication.category}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  // Main catalog view
  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
            Medication Catalog
          </h1>
          <p className="text-gray-600 mt-2">Comprehensive medication database with pricing, margins, and business intelligence</p>
        </div>
        <div className="flex space-x-3">
          <button className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 flex items-center space-x-2">
            <Download className="h-4 w-4" />
            <span>Export</span>
          </button>
          <button className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 flex items-center space-x-2">
            <Settings className="h-4 w-4" />
            <span>Settings</span>
          </button>
          <button className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 flex items-center space-x-2">
            <Plus className="h-4 w-4" />
            <span>Add Medication</span>
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-600 mb-1">Total Medications</p>
              <p className="text-3xl font-bold text-gray-900">{totalMedications}</p>
            </div>
            <div className="p-3 rounded-xl bg-blue-100">
              <Package className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-600 mb-1">Average Margin</p>
              <p className="text-3xl font-bold text-gray-900">{averageMargin.toFixed(1)}%</p>
            </div>
            <div className="p-3 rounded-xl bg-green-100">
              <TrendingUp className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-600 mb-1">Monthly Volume</p>
              <p className="text-3xl font-bold text-gray-900">{totalMonthlyVolume.toLocaleString()}</p>
            </div>
            <div className="p-3 rounded-xl bg-purple-100">
              <Activity className="h-6 w-6 text-purple-600" />
            </div>
          </div>
        </div>

        <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-600 mb-1">High Margin Items</p>
              <p className="text-3xl font-bold text-gray-900">{highMarginMeds}</p>
            </div>
            <div className="p-3 rounded-xl bg-orange-100">
              <Target className="h-6 w-6 text-orange-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Controls */}
      <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              placeholder="Search medications..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
          
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            <option value="all">All Categories</option>
            {categories.map(category => (
              <option key={category} value={category}>{category}</option>
            ))}
          </select>

          <select
            value={selectedSchedule}
            onChange={(e) => setSelectedSchedule(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            <option value="all">All Schedules</option>
            {schedules.map(schedule => (
              <option key={schedule} value={schedule}>{schedule.toUpperCase()}</option>
            ))}
          </select>

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            <option value="profitabilityRank">Profitability Rank</option>
            <option value="grossMargin">Gross Margin</option>
            <option value="monthlyVolume">Monthly Volume</option>
            <option value="name">Name A-Z</option>
            <option value="retailPrice">Price (High-Low)</option>
          </select>

          <div className="flex items-center space-x-2">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={showPricing}
                onChange={(e) => setShowPricing(e.target.checked)}
                className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
              />
              <span className="ml-2 text-sm text-gray-700">Show Pricing</span>
            </label>
          </div>

          <div className="flex space-x-2">
            <button
              onClick={() => setViewMode('table')}
              className={`p-2 rounded-lg ${viewMode === 'table' ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-600'}`}
            >
              <BarChart3 className="h-4 w-4" />
            </button>
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-lg ${viewMode === 'grid' ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-600'}`}
            >
              <Package className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Medication List */}
      <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50/50">
              <tr>
                <th className="px-4 py-4 text-left text-sm font-medium text-gray-900">Medication</th>
                <th className="px-2 py-4 text-left text-sm font-medium text-gray-900">Category</th>
                <th className="px-2 py-4 text-left text-sm font-medium text-gray-900">Schedule</th>
                {showPricing && (
                  <>
                    <th className="px-2 py-4 text-left text-sm font-medium text-gray-900">COGS</th>
                    <th className="px-3 py-4 text-left text-sm font-medium text-gray-900">Retail Price</th>
                    <th className="px-2 py-4 text-left text-sm font-medium text-gray-900">Margin</th>
                  </>
                )}
                <th className="px-3 py-4 text-left text-sm font-medium text-gray-900">Volume</th>
                <th className="px-3 py-4 text-left text-sm font-medium text-gray-900">Stock</th>
                <th className="px-2 py-4 text-left text-sm font-medium text-gray-900">Rank</th>
                <th className="px-4 py-4 text-left text-sm font-medium text-gray-900">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredMedications.map((medication) => (
                <tr key={medication.id} className="hover:bg-gray-50/50">
                  <td className="px-4 py-4">
                    <div>
                      <p className="font-medium text-gray-900">{medication.name}</p>
                      <p className="text-sm text-gray-600">{medication.genericName}</p>
                      <p className="text-sm text-gray-500">{medication.brandName} • {medication.manufacturer}</p>
                      {medication.pbsListed && (
                        <span className="inline-flex px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full mt-1">
                          PBS Listed
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-2 py-4">
                    <span className={`px-3 py-1 text-sm font-medium rounded-full ${getCategoryColor(medication.category)}`}>
                      {medication.category}
                    </span>
                  </td>
                  <td className="px-2 py-4">
                    <span className={`px-3 py-1 text-sm font-medium rounded border ${getScheduleBadgeClass(medication.schedule)}`}>
                      {medication.schedule === 'unscheduled' ? 'Unscheduled' : medication.schedule.toUpperCase()}
                    </span>
                  </td>
                  {showPricing && (
                    <>
                      <td className="px-2 py-4 font-medium text-gray-900">
                        ${medication.costOfGoodsSold.toFixed(2)}
                      </td>
                      <td className="px-3 py-4 font-medium text-gray-900">
                        ${medication.retailPrice.toFixed(2)}
                      </td>
                      <td className="px-2 py-4">
                        <div className="flex items-center space-x-2">
                          <span className="font-medium text-gray-900">{medication.grossMargin.toFixed(1)}%</span>
                          {medication.grossMargin > 40 ? (
                            <TrendingUp className="h-4 w-4 text-green-500" />
                          ) : medication.grossMargin < 25 ? (
                            <TrendingDown className="h-4 w-4 text-red-500" />
                          ) : (
                            <Activity className="h-4 w-4 text-yellow-500" />
                          )}
                        </div>
                      </td>
                    </>
                  )}
                  <td className="px-3 py-4">
                    <div>
                      <p className="font-medium text-gray-900">{medication.monthlyVolume}</p>
                      <p className="text-sm text-gray-600">units/month</p>
                    </div>
                  </td>
                  <td className="px-3 py-4">
                    <div>
                      <p className="font-medium text-gray-900">{Math.floor(Math.random() * medication.maximumStock)}</p>
                      <div className="flex items-center space-x-1 mt-1">
                        <div className="w-16 bg-gray-200 rounded-full h-1">
                          <div 
                            className="bg-green-500 h-1 rounded-full" 
                            style={{ width: `${Math.random() * 100}%` }}
                          ></div>
                        </div>
                        <span className="text-xs text-gray-500">/{medication.maximumStock}</span>
                      </div>
                    </div>
                  </td>
                  <td className="px-2 py-4">
                    <div className="flex items-center space-x-2">
                      <span className={`font-medium ${getProfitabilityColor(medication.profitabilityRank)}`}>
                        #{medication.profitabilityRank}
                      </span>
                      {medication.profitabilityRank <= 10 && (
                        <CheckCircle className="h-4 w-4 text-green-500" />
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-2">
                      <button className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg">
                        <Eye className="h-4 w-4" />
                      </button>
                      <button className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg">
                        <Edit className="h-4 w-4" />
                      </button>
                      <button className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg">
                        <DollarSign className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Results Summary */}
      <div className="flex items-center justify-between text-sm text-gray-600">
        <p>Showing {filteredMedications.length} of {totalMedications} medications</p>
        <div className="flex items-center space-x-4">
          <p>Average margin: {(filteredMedications.reduce((sum, med) => sum + med.grossMargin, 0) / filteredMedications.length).toFixed(1)}%</p>
          <p>Total monthly volume: {filteredMedications.reduce((sum, med) => sum + med.monthlyVolume, 0).toLocaleString()}</p>
        </div>
      </div>
    </div>
  );
};

export default Catalog;
